import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Plus, Pencil, X } from "lucide-react"
import { toast } from "sonner"
import { api, type ApiEnvelope, type Product } from "@/lib/api"
import { getErrorMessage, getFieldErrors } from "@/lib/get-error-message"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { FieldError } from "@/components/ui/field-error"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { ConfirmDelete } from "@/components/admin/confirm-delete"
import { ImageUpload } from "@/components/admin/image-upload"
import { RichTextEditor } from "@/components/admin/rich-text-editor"

export const Route = createFileRoute("/admin/_authed/products")({
  component: ProductsPage,
})

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
}

type FormState = {
  title: string
  slug: string
  tagline: string
  description: string
  details_content: string
  price_label: string
  features: string[]
  cta_text: string
  cta_link: string
  order: number
  is_active: boolean
}
const empty: FormState = {
  title: "",
  slug: "",
  tagline: "",
  description: "",
  details_content: "",
  price_label: "",
  features: [],
  cta_text: "Get started",
  cta_link: "",
  order: 0,
  is_active: true,
}

function ProductsPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery<Product[]>({
    queryKey: ["admin", "products"],
    queryFn: async () => (await api.get<ApiEnvelope<Product[]>>("/admin/products")).data.data,
  })

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Product | null>(null)
  const [form, setForm] = useState<FormState>(empty)
  const [slugTouched, setSlugTouched] = useState(false)
  const [image, setImage] = useState<File | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "products"] })
    queryClient.invalidateQueries({ queryKey: ["products"] })
  }

  const save = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      fd.append("title", form.title)
      fd.append("slug", form.slug)
      fd.append("tagline", form.tagline)
      fd.append("description", form.description)
      fd.append("details_content", form.details_content)
      fd.append("price_label", form.price_label)
      fd.append("cta_text", form.cta_text)
      fd.append("cta_link", form.cta_link)
      fd.append("order", String(form.order))
      fd.append("is_active", form.is_active ? "1" : "0")
      form.features
        .map((f) => f.trim())
        .filter((f) => f.length > 0)
        .forEach((f, i) => fd.append(`features[${i}]`, f))
      if (image) fd.append("image", image)
      if (editing) {
        fd.append("_method", "PUT")
        await api.post(`/admin/products/${editing.slug}`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      } else {
        await api.post("/admin/products", fd, { headers: { "Content-Type": "multipart/form-data" } })
      }
    },
    onSuccess: () => {
      invalidate()
      setOpen(false)
      setFieldErrors({})
      toast.success(editing ? "Product updated." : "Product created.")
    },
    onError: (error) => {
      setFieldErrors(getFieldErrors(error))
      toast.error(getErrorMessage(error))
    },
  })

  const remove = useMutation({
    mutationFn: async (slug: string) => api.delete(`/admin/products/${slug}`),
    onSuccess: () => {
      invalidate()
      toast.success("Product deleted.")
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreate = () => {
    setEditing(null)
    setForm(empty)
    setSlugTouched(false)
    setImage(null)
    setFieldErrors({})
    setOpen(true)
  }
  const openEdit = (item: Product) => {
    setEditing(item)
    setSlugTouched(true)
    setForm({
      title: item.title,
      slug: item.slug,
      tagline: item.tagline ?? "",
      description: item.description ?? "",
      details_content: item.details_content ?? "",
      price_label: item.price_label ?? "",
      features: item.features ?? [],
      cta_text: item.cta_text ?? "",
      cta_link: item.cta_link ?? "",
      order: item.order,
      is_active: item.is_active,
    })
    setImage(null)
    setFieldErrors({})
    setOpen(true)
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Products</h1>
          <p className="mt-1 text-sm text-muted-foreground">Products shown on the public products page.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-full">
              <Plus className="h-4 w-4" /> New product
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Edit product" : "New product"}</DialogTitle>
            </DialogHeader>
            <form
              className="space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                save.mutate()
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={form.title}
                    onChange={(e) => {
                      const title = e.target.value
                      setForm((f) => ({ ...f, title, slug: slugTouched ? f.slug : slugify(title) }))
                    }}
                    aria-invalid={!!fieldErrors.title}
                  />
                  <FieldError message={fieldErrors.title} />
                </div>
                <div>
                  <Label htmlFor="slug">Slug</Label>
                  <Input
                    id="slug"
                    value={form.slug}
                    onChange={(e) => {
                      setSlugTouched(true)
                      setForm({ ...form, slug: e.target.value })
                    }}
                    placeholder="auto-generated from title"
                    aria-invalid={!!fieldErrors.slug}
                  />
                  <FieldError message={fieldErrors.slug} />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="tagline">Tagline</Label>
                  <Input id="tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="price_label">Price label</Label>
                  <Input
                    id="price_label"
                    placeholder="e.g. Starting at $99/mo"
                    value={form.price_label}
                    onChange={(e) => setForm({ ...form, price_label: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="description">Short description</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Shown on the products list card and used as the SEO/social-share description.
                </p>
              </div>
              <ImageUpload label="Image" currentUrl={editing?.image_url} onChange={setImage} />
              <div>
                <Label>Product details</Label>
                <p className="mb-2 text-xs text-muted-foreground">
                  The full write-up shown on this product's own details page — screenshots, use cases, anything
                  beyond the short description above.
                </p>
                <RichTextEditor
                  value={form.details_content}
                  onChange={(details_content) => setForm((f) => ({ ...f, details_content }))}
                />
              </div>
              <div>
                <Label>Feature checklist</Label>
                <div className="mt-2 space-y-2">
                  {form.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        value={feature}
                        placeholder="e.g. Real-time sync"
                        onChange={(e) => {
                          const features = [...form.features]
                          features[i] = e.target.value
                          setForm({ ...form, features })
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setForm({ ...form, features: form.features.filter((_, idx) => idx !== i) })}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setForm({ ...form, features: [...form.features, ""] })}
                  disabled={form.features.length >= 8}
                >
                  <Plus className="h-4 w-4" /> Add feature
                </Button>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="cta_text">CTA text</Label>
                  <Input id="cta_text" value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} placeholder="Learn more" />
                </div>
                <div>
                  <Label htmlFor="cta_link">CTA link</Label>
                  <Input id="cta_link" value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} placeholder="https://..." />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 sm:items-end">
                <div>
                  <Label htmlFor="order">Order</Label>
                  <Input
                    id="order"
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                  />
                </div>
                <div className="flex items-center gap-3 pb-2">
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} id="is_active" />
                  <Label htmlFor="is_active">Active</Label>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-secondary" />
        ) : isError ? (
          <p className="text-sm text-destructive">Couldn't load products.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(data ?? []).map((p) => (
              <div key={p.id} className="overflow-hidden rounded-2xl border border-border bg-card">
                <div className="aspect-[16/9] bg-secondary">
                  {p.image_url && <img src={p.image_url} alt={p.title} className="h-full w-full object-cover" />}
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <h3 className="font-semibold">{p.title}</h3>
                    <Badge variant={p.is_active ? "default" : "secondary"}>{p.is_active ? "Active" : "Hidden"}</Badge>
                  </div>
                  {p.tagline && <p className="mt-1 text-xs text-rust">{p.tagline}</p>}
                  {p.description && <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{p.description}</p>}
                  <div className="mt-4 flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(p)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <ConfirmDelete itemLabel={p.title} onConfirm={() => remove.mutate(p.slug)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
